import {
    Active,
    DndContext,
    DragEndEvent,
    DragOverlay,
    DragStartEvent,
    PointerSensor,
    useSensor,
    useSensors
} from '@dnd-kit/core';
import {Col, Layout, message, Row, Spin} from 'antd';
import _ from 'lodash';
import React, {useEffect, useRef, useState} from "react";
import ReactDOM from "react-dom";
import {useHistory, useLocation, useParams} from "react-router-dom";
import {apiFetchQueryResult, apiFetchUserQuery} from '../../containers/QueryBuilder/queryBuilderApi';
import {Field} from '../../containers/QueryBuilder/types/Field';
import {Query, QueryField, QueryResult, QueryStatus} from '../../containers/QueryBuilder/types/Query';
import {UserQuery} from '../../containers/QueryBuilder/types/UserQuery';
import {getQueryBuilderCohortAnalyserRoute, QUERY_ID} from '../../pages/routes';
import styles from "./QueryBuilder.module.scss";
import {CurrentField, emptyUserQuery, OnFilterFinishType, QueryBuilderContext} from './QueryBuilderContext';
import {QueryBuilderTemplate} from './QueryBuilderTemplate';
import {QueryInputList, removeItem} from "./QueryInputList";
import DatasetList from './components/DatasetList';
import {FieldItem} from './components/FieldItem';
import {FieldList} from "./components/FieldList";
import {FilterDetails} from './components/FilterDetails';
import FilterDialog from './components/FilterDialogs/FilterDialog';
import ResultPanel from './components/ResultPanel';
import ValueDialog, {
    AGG_CHOICES,
    availableChoices,
    IDENTIFIER_CHOICE,
    SUM_CHOICE
} from './components/ValueDialog';
import {fieldChoices, isCountOnlyAgg} from "./QueryBuilderUtil";
import {useAppSelector} from "../../hooks";
import {selectFields} from "../../containers/QueryBuilder/queryBuilderSlice";
import {LabelAndKey} from "../create/types/LabelAndKey";
import {apiFetchOptions} from "../create/selectorApi";
import {cohortDescription} from "./components/FilterDialogs/CohortsFilter";
import {refreshSession} from "../../actions/api";
import {ReactComponent as RowLabelsIcon} from './icons/RowLabels.svg';
import {ReactComponent as ColumnLabelsIcon} from './icons/ColumnLabels.svg';
import {ReactComponent as ValuesIcon} from './icons/Values.svg';
import {ReactComponent as ReportFiltersIcon} from './icons/ReportFilters.svg';
import {QueriesDropDown} from "./QueriesDropDown";
import NewQueryButton from "./NewQueryButton";
import {hasCompanyQueryHistoryViewPermission} from "../../actions/userSlice";
import {ChartOptions, ChartResults} from "../../actions/queryBuilderApi";
import {Visualisations} from "../../containers/QueryBuilder/visualisations/Visualisations";
import {rawContentHasEntries} from "./components/ResultTable";

import {QueryMetaData} from "../../containers/QueryBuilder/types/QueryMetaData";
import {QUERY_BUILDER_TAB} from "./QueryBuilderTabs";
import SaveQueryToggle from "./SaveQueryToggle";

const {Sider, Content} = Layout

const UPDATE_INTERVAL = 1000 * 10

export const LIST = 'list'
export const ROW = 'row'
export const COLUMN = 'column'
export const VALUE = 'value'
export const FILTER = 'filter'

export const addFieldWhenValid = (valid: boolean, typeOfField: string, list: QueryField[], field: Field, aggtype?: string): boolean => {
    const queryField: QueryField = {
        code: field.code,
        type: field.type,
        aggtype: aggtype,
        distinct: field.distinct
    }
    if (valid) {

        const isValueField = typeOfField.includes(VALUE)

        const isNotPresentInQuery =
            isValueField
                ? _.isNil(_.find(list, {code: queryField.code, aggtype: aggtype}))
                : _.isNil(_.find(list, {code: queryField.code}))

        if (isNotPresentInQuery) {
            list.push(queryField)
            return true
        } else {
            message.warn(`${field.name} has already been added.`)
        }
    } else {
        message.warn(`Cannot add ${field.name}.`)
    }
    return false
}

export const asQueryString = (query: Query) => {
    return JSON.stringify({
        rows: query.rows,
        columns: query.columns,
        values: query.values,
        filters: query.filters,
    })

}

export const queryHasData = (query: Query) => {
    return query.rows.length > 0
        || query.columns.length > 0
        || query.values.length > 0
        || query.filters.length > 0
}

const COHORTS_CODE = 'cohorts'
const COHORTS_PERIOD = 'period'
const COHORTS_MONTHS = 'months'

export const cohortPeriodField = (fieldId: string) => {
    return `${fieldId}${COHORTS_PERIOD}`
}

export const cohortMonthsField = (fieldId: string) => {
    return `${fieldId}${COHORTS_MONTHS}`
}

export const isCohortField = (code: string) => {
    return code.startsWith(COHORTS_CODE)
}

export const isValidCohortID = (cohortId: number | null): boolean => {
    return !!(cohortId && cohortId > 0)
}

export const addCohortFilter = (userQuery: UserQuery, cohortId: number, cohortChoices: LabelAndKey[]): QueryField => {
    const queryFields = userQuery.query.filters.filter(i => i.code === COHORTS_CODE)
    if (queryFields.length === 0) {
        const code = COHORTS_CODE
        const period = 'before'
        const month = '120'
        const cohorts: QueryField = {
            code: code,
            type: 'IP',
            distinct: false,
            value: cohortId ? '' + cohortId : undefined,
        }
        userQuery.query.filters.push(cohorts)
        userQuery.query.filters.push({
            code: `${COHORTS_CODE}${COHORTS_PERIOD}`,
            type: 'IP',
            distinct: false,
            value: period,
        })
        userQuery.query.filters.push({
            code: `${COHORTS_CODE}${COHORTS_MONTHS}`,
            type: 'IP',
            distinct: false,
            value: month,
        })
        const description = cohortDescription(code, period, month, ['' + cohortId], cohortChoices)

        if (userQuery.query.filterLabels) {
            userQuery.query.filterLabels.push(description)
        }


        return cohorts
    } else {
        return queryFields[0]
    }
}

export const hasCohortAnalyserInPath = (location: Location): boolean => {
    return location && location.pathname.includes('/cohort-analyser')
}

export const canRunQuery = (query: Query, isCohortAnalyser: boolean) => {
    let isValid = query.values.length > 0
    if (isCohortAnalyser) {
        isValid = isValid && query.filters.filter(i => isCohortField(i.code) && i.value && i.value.length > 0).length > 0
    }
    return isValid
}

export const QueryBuilder = () => {

    const [tableStatus, setTableStatus] = useState(false);

    const toggleCollapse = () => {
        setTableStatus(prev => !prev);
    };

    const collapseFullViewIfOpened = () => {
        if (tableStatus) {
            toggleCollapse()
        }
    }

    const {cohortId} = useParams<{ cohortId: number }>()
    const location = useLocation()
    const isCohortAnalyser = hasCohortAnalyserInPath(location)
    const params = new URLSearchParams(location.search)
    const queryId = params.get(QUERY_ID)

    const [dataset, setDataset] = useState<string>("IP")
    const [currentField, setCurrentField] = useState<CurrentField>(undefined)
    const [isValueModalOpen, setIsValueModalOpen] = useState(false)
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
    const [onFilterFinish, setOnFilterFinish] = useState<OnFilterFinishType>(undefined)
    const [id, setId] = useState<string | undefined>(queryId ? queryId : undefined)
    const activeQueryIdRef = useRef<string | undefined>(id)
    const [queryResult, setQueryResult] = useState<QueryResult | undefined>(undefined)
    const [active, setActive] = useState<Active | null>(null)
    const [cohortChoices, setCohortChoices] = useState<LabelAndKey[] | undefined>(undefined)

    const [userQuery, setUserQuery] = useState<UserQuery>(emptyUserQuery())
    const userQueryRef = useRef(userQuery)
    const fields = useAppSelector(selectFields)!

    const [row, setRow] = useState(true)
    const [column, setColumn] = useState(true)
    const [value, setValue] = useState(true)
    const [filter, setFilter] = useState(true)

    const [defaultQueryVisualisationOptions, setDefaultQueryVisualisationOptions] = useState<ChartOptions | undefined>(undefined)
    const [defaultQueryVisualisationResults, setDefaultQueryVisualisationResults] = useState<ChartResults | undefined>(undefined)

    const [queryMetaData, setQueryMetaData] = useState<QueryMetaData | undefined>(undefined)

    const [isQueryVisualizerTabEnabled, setQueryVisualizerTabEnabled] = useState(false)

    const [queryBuilderTab, setQueryBuilderTab] = useState<QUERY_BUILDER_TAB>(QUERY_BUILDER_TAB.QUERY_BUILDER);

    // region utility functions
    const refreshQueryResult = () => {
        if (id && !queryResult && QueryStatus.FINISHED === userQuery.status) {
            const requestedQueryId = id

            apiFetchQueryResult(requestedQueryId)
                .then(qr => {
                    if (activeQueryIdRef.current !== requestedQueryId) {
                        return
                    }

                    if (!qr) {
                        _.delay(() => {
                            if (activeQueryIdRef.current === requestedQueryId) {
                                refreshQueryResult()
                            }
                        }, UPDATE_INTERVAL)
                    } else {
                        setQueryResult(qr)
                    }
                })
        }
    }

    useEffect(() => {
        activeQueryIdRef.current = id
    }, [id])

    useEffect(() => {
        if (cohortId) {
            apiFetchOptions('/cohorts?cohortId=' + cohortId, setCohortChoices)
        }
    }, [cohortId])

    useEffect(() => {
        userQueryRef.current = userQuery
        refreshQueryResult()
    }, [userQuery.status]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (fields && cohortChoices && isCohortAnalyser) {
            ReactDOM.unstable_batchedUpdates(() => {
                const qf = addCohortFilter(userQuery, cohortId, cohortChoices)
                setUserQuery(userQuery)
                setCurrentField({field: fields[qf.code], queryField: qf})
                setIsFilterModalOpen(true)
            })
        }
    }, [fields, cohortChoices]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {

        const {status, query} = userQuery

        if (status !== QueryStatus.FINISHED) {
            setQueryVisualizerTabEnabled(false)
            return
        }

        if (status === QueryStatus.FINISHED) {

            if (!queryResult) {
                setQueryVisualizerTabEnabled(false)
                return
            }

            if (queryResult.valueAvailable && rawContentHasEntries(queryResult)) {
                if (query.rows.length > 0 || query.columns.length > 0) {
                    if (defaultQueryVisualisationResults && defaultQueryVisualisationResults.values.length > 0) {
                        setQueryVisualizerTabEnabled(true)
                        return
                    }
                }
            } else if ((!queryResult.valueAvailable && userQuery.errorMsg === 'DISPLAY_CAPACITY_EXCEEDED') || userQuery.largeExport) {
                setQueryVisualizerTabEnabled(false)
            } else {
                setQueryVisualizerTabEnabled(false)
            }
        }

    }, [queryResult, userQuery, defaultQueryVisualisationResults])

    const refreshUserQuery = () => {
        if (id && userQuery && QueryStatus.RUNNING === userQuery.status) {
            const requestedQueryId = id

            apiFetchUserQuery(requestedQueryId)
                .then(uq => {
                    if (activeQueryIdRef.current !== requestedQueryId) {
                        return
                    }

                    if (uq) {
                        if (![QueryStatus.FINISHED, QueryStatus.ERROR, QueryStatus.CANCELLED].includes(uq.status)) {
                            _.delay(() => {
                                if (activeQueryIdRef.current === requestedQueryId) {
                                    refreshUserQuery()
                                }
                            }, UPDATE_INTERVAL)
                        }

                        if (userQuery === userQueryRef.current) {
                            queryStringRef.current = asQueryString(uq.query)
                            setUserQuery(uq)
                        }
                    } else {
                        _.delay(function (funk: () => void) {
                            funk()
                        }, UPDATE_INTERVAL, refreshUserQuery)
                    }
                })
        }
    }

    useEffect(() => {
        refreshUserQuery()
    }, [userQuery.status]) // eslint-disable-line react-hooks/exhaustive-deps

    const queryStringRef = useRef<undefined | string>(undefined)
    useEffect(() => {
        if (id) {
            apiFetchUserQuery(id)
                .then(q => {
                    queryStringRef.current = asQueryString(q.query)
                    setUserQuery(q)
                    setDataset(q.query.values[0].type)
                })
        }
    }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

    const queryString = asQueryString(userQuery.query)
    useEffect(() => {
        if (queryStringRef.current
            && queryString !== queryStringRef.current
            && [QueryStatus.FINISHED, QueryStatus.ERROR, QueryStatus.CANCELLED].includes(userQuery.status)
            && queryHasData(userQuery.query)
        ) {
            ReactDOM.unstable_batchedUpdates(() => {
                setId(undefined)
                setQueryResult(undefined)
                if (canRunQuery(userQuery.query, isCohortAnalyser)) {
                    userQuery.status = QueryStatus.READY
                } else {
                    userQuery.status = QueryStatus.NOT_READY
                }
                setUserQuery(userQuery)
                queryStringRef.current = queryString
                history.push(getQueryBuilderCohortAnalyserRoute(isCohortAnalyser, cohortId))
            })
        }
    }, [queryString]) // eslint-disable-line react-hooks/exhaustive-deps

    const onDragStart = (event: DragStartEvent) => {
        // keeps session active. Can be removed if we cache on backend queries as they are built
        refreshSession().catch(console.error);
        const field = event.active.data.current?.field as Field
        ReactDOM.unstable_batchedUpdates(() => {
            const {row, column, value, filter} = fieldChoices(field)

            setRow(row)
            setColumn(column)
            setValue(value)
            setFilter(filter)

            setActive(event.active)
        })
    }

    const onDragEnd = (event: DragEndEvent) => {
        setActive(null)
        if (event.over) {
            const from = event.active.data.current?.from
            const field = event.active.data.current?.field as Field

            let valueResult = false
            switch (event.over.id) {
                case ROW:
                    valueResult = addFieldWhenValid(row, ROW, userQuery.query.rows, field)
                    break
                case COLUMN:
                    valueResult = addFieldWhenValid(column, COLUMN, userQuery.query.columns, field)
                    break
                case VALUE:
                    let aggtype = AGG_CHOICES[0].value
                    if (isCountOnlyAgg(field)) {
                        aggtype = IDENTIFIER_CHOICE.value
                    } else {
                        if (field.simpleAggregation) {
                            aggtype = SUM_CHOICE.value
                        } else {
                            const choices = availableChoices(field.code, userQuery.query.values)
                            if (choices && choices.length > 0) {
                                aggtype = choices[0].value
                            }
                        }
                    }

                    valueResult = addFieldWhenValid(value, VALUE, userQuery.query.values, field, aggtype)
                    if (valueResult && !(field.simpleAggregation)) {
                        setCurrentField({
                            field: field,
                            queryField: {
                                code: field.code,
                                type: field.type,
                                distinct: field.distinct,
                                aggtype: aggtype,
                            }
                        })
                        setIsValueModalOpen(true)
                    }
                    break
                case FILTER:
                    if (isCohortField(field.code)) {
                        const existingCohortFilter = userQuery
                            .query
                            .filters
                            .find(i => isCohortField(i.code))
                        if (existingCohortFilter) {
                            message.warn("A cohort filter is already setup.")
                            break
                        }
                    }
                    valueResult = addFieldWhenValid(filter, FILTER, userQuery.query.filters, field)
                    if (valueResult) {
                        setCurrentField({field: field})
                        setIsFilterModalOpen(true)
                    }
                    break
                default:
                    message.warn("No match exists!")
                    break
            }
            if (valueResult) {
                const qf = {code: field.code, type: field.type, distinct: field.distinct}
                removeItem(from, userQuery, qf)
            }
            setUserQuery(userQuery)
            if (canRunQuery(userQuery.query, isCohortAnalyser)) {
                userQuery.status = QueryStatus.READY
                setUserQuery(userQuery)
            }
        }
    }

    const history = useHistory()

    const newQuery = () => {
        activeQueryIdRef.current = undefined

        ReactDOM.unstable_batchedUpdates(() => {
            setId(undefined)
            const emptyQuery = emptyUserQuery()
            if (isValidCohortID(cohortId)) {
                addCohortFilter(emptyQuery, cohortId, cohortChoices!)
            }
            history.push(getQueryBuilderCohortAnalyserRoute(isCohortAnalyser, cohortId))
            setUserQuery(emptyQuery)
            setQueryResult(undefined)
        })
        collapseFullViewIfOpened();
    }

    const hasCompanyQueryHistoryAccess = useAppSelector(hasCompanyQueryHistoryViewPermission)

    const buttons = [
        <NewQueryButton key='1' onClick={() => {
            newQuery()
        }}/>,
        ...(queryResult?.valueAvailable ? [
            <SaveQueryToggle key='2' queryId={id} userQuery={userQuery} setUserQuery={setUserQuery} onlyShowSavedQueryEditButton={false}/>
        ] : []),
        <QueriesDropDown key='3' isCohortAnalyser={isCohortAnalyser}
                         hasCompanyQueryHistoryAccess={hasCompanyQueryHistoryAccess}
        />
    ]

    const mouseSensor = useSensor(PointerSensor, {
        activationConstraint: {
            distance: 8,
        },
    })
    const sensors = useSensors(mouseSensor)

    if (!fields) {
        return <Spin/>
    }

    // endregion utility functions

    return <>

        <QueryBuilderContext.Provider value={{
            id, setId, cohortId, isCohortAnalyser, userQuery, dataset, setDataset, setUserQuery, queryResult,
            setQueryResult, currentField, setCurrentField, isValueModalOpen, setIsValueModalOpen,
            isFilterModalOpen, setIsFilterModalOpen, onFilterFinish, setOnFilterFinish,
            setDefaultQueryVisualisationOptions,
            defaultQueryVisualisationResults,
            setDefaultQueryVisualisationResults,
            queryMetaData, setQueryMetaData
        }}>

            <div>
                <div style={{ display: queryBuilderTab === QUERY_BUILDER_TAB.QUERY_BUILDER ? 'block' : 'none' }}>
                    <>
                        <QueryBuilderTemplate buttons={buttons}
                                              isCohortAnalyser={isCohortAnalyser}
                                              setQueryBuilderTab={setQueryBuilderTab}
                                              isQueryVisualizerTabEnabled={isQueryVisualizerTabEnabled}
                        >

                            <DndContext onDragStart={onDragStart} onDragEnd={onDragEnd} sensors={sensors}>

                                <DragOverlay dropAnimation={null}>
                                    {active?.data?.current ? (
                                        <FieldItem
                                            id={active.data.current.id as string}
                                            field={active.data.current.field as Field}
                                            isOverlay
                                        />
                                    ) : null}
                                </DragOverlay>

                                {!tableStatus &&
                                    <Sider width={250} className={styles.sider}>
                                        <DatasetList/>
                                        <FieldList/>
                                    </Sider>}

                                <Content>

                                    {
                                        !tableStatus &&

                                        <Row gutter={16} style={{marginRight: '0px'}}>

                                            <Col span={6}>
                                                <QueryInputList id={COLUMN} values={userQuery.query.columns}
                                                                title={
                                                                    <div className={styles.sectionHeader}>
                                                                        <RowLabelsIcon className={styles.sectionIcon}/>
                                                                        <span className={styles.sectionText}>Row Labels</span>
                                                                    </div>}
                                                                enabled={row} dataTestId={'query-rows-section'}/>
                                            </Col>

                                            <Col span={6}>
                                                <QueryInputList id={ROW} values={userQuery.query.rows}
                                                                title={
                                                                    <div className={styles.sectionHeader}>
                                                                        <ColumnLabelsIcon className={styles.sectionIcon}/>
                                                                        <span
                                                                            className={styles.sectionText}>Column Labels</span>
                                                                    </div>}
                                                                enabled={column} dataTestId={'query-columns-section'}/>
                                            </Col>

                                            <Col span={6}>
                                                <QueryInputList id={VALUE} values={userQuery.query.values}
                                                                title={
                                                                    <div className={styles.sectionHeader}>
                                                                        <ValuesIcon className={styles.sectionIcon}/>
                                                                        <span className={styles.sectionText}>Values</span>
                                                                    </div>}
                                                                enabled={value} dataTestId={'query-values-section'}/>
                                            </Col>

                                            <Col span={6}>
                                                <QueryInputList id={FILTER} values={userQuery.query.filters}
                                                                title={
                                                                    <div className={styles.sectionHeader}>
                                                                        <ReportFiltersIcon className={styles.sectionIcon}/>
                                                                        <span
                                                                            className={styles.sectionText}>Report Filters</span>
                                                                    </div>}
                                                                enabled={filter}
                                                                additionalClazz={styles.no_right_margin}
                                                                dataTestId={'query-filters-section'}/>
                                            </Col>

                                        </Row>
                                    }

                                    {
                                        !tableStatus &&

                                        <Row>
                                            <Col span={24}>
                                                <FilterDetails/>
                                            </Col>
                                        </Row>
                                    }

                                    <Row>
                                        <Col span={24}>
                                            <ResultPanel queryId={id}
                                                         setUserQuery={setUserQuery}
                                                         isExpanded={tableStatus}
                                                         toggleCollapse={toggleCollapse}
                                                         userQuery={userQuery}
                                                         isCohortAnalyser={isCohortAnalyser}
                                            />
                                        </Col>
                                    </Row>

                                </Content>

                            </DndContext>

                            {isValueModalOpen ? <ValueDialog/> : null}
                            {isFilterModalOpen ? <FilterDialog/> : null}

                        </QueryBuilderTemplate>
                    </>
                </div>

                <div style={{ display: queryBuilderTab === QUERY_BUILDER_TAB.QUERY_VISUALISER ? 'block' : 'none' }}>
                    <Visualisations key={id} queryId={id}
                                    queryMetaData={queryMetaData}
                                    defaultQueryVisualisationOptions={defaultQueryVisualisationOptions}
                                    defaultQueryVisualisationResults={defaultQueryVisualisationResults}
                                    setQueryBuilderTab={setQueryBuilderTab}
                                    isQueryVisualizerTabEnabled={isQueryVisualizerTabEnabled}
                                    isCohortAnalyser={isCohortAnalyser}
                    />
                </div>

            </div>

        </QueryBuilderContext.Provider>

    </>

}